import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "user-posts-container" }
const _hoisted_2 = { class: "status-filter" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = {
  key: 0,
  class: "loading"
}
const _hoisted_5 = {
  key: 1,
  class: "no-data"
}
const _hoisted_6 = {
  key: 2,
  class: "posts-list"
}
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "post-header" }
const _hoisted_9 = ["src"]
const _hoisted_10 = { class: "user-info" }
const _hoisted_11 = { class: "username" }
const _hoisted_12 = { class: "post-time" }
const _hoisted_13 = { class: "post-content" }
const _hoisted_14 = { class: "post-title" }
const _hoisted_15 = { class: "post-text" }
const _hoisted_16 = ["src"]
const _hoisted_17 = { class: "post-stats" }
const _hoisted_18 = { class: "stat-item" }
const _hoisted_19 = { class: "stat-item" }
const _hoisted_20 = { class: "stat-item" }
const _hoisted_21 = { class: "stat-item" }
const _hoisted_22 = {
  key: 0,
  class: "action-buttons"
}
const _hoisted_23 = ["onClick"]
const _hoisted_24 = ["onClick"]

import { ref, onMounted, computed, nextTick } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { View, StarFilled, ChatLineRound, Collection } from '@element-plus/icons-vue';
import { getUserPosts } from '@/user/api/userApi';  // 从userApi导入获取用户帖子列表
import { deletePost } from '@/user/api/post'; // 从正确的API文件导入deletePost函数
import { formatPostDate } from '@/utils/date';
import { useStore } from 'vuex'; // 导入store
import EditPostModal from './EditPostModal.vue'; // 引入新的编辑模态框组件

interface Post {
  id: number;
  userId: number;
  name: string;
  userAvatar: string;
  title: string;
  content: string;
  coverImage?: string;
  status: number;
  createTime: string;
  viewCount: number;
  likeCount: number;
  favoriteCount: number;
  commentCount: number;
  isLiked: number;
  isFavorited: number;
  isFollowed: number;
  hotComment: any;
  comments: any[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'UserPostsView',
  setup(__props) {

const posts = ref<Post[]>([]);
const loading = ref(true);
const currentPage = ref(1);
const total = ref(0);
const pageSize = ref(5);

// 编辑帖子相关
const editDialogVisible = ref(false);
const editingPostId = ref<number | null>(null);

// 状态筛选相关
const statuses = [
  { label: '全部', value: -1 },
  { label: '已发布', value: 1 },
  { label: '待审核', value: 0 },
  { label: '已驳回', value: 2 },
  { label: '已封禁', value: 3 }
];

const selectedStatus = ref(-1); // 默认选中全部

// 从store获取当前用户ID
const store = useStore();
const currentUserId = computed(() => store.state.userInfo?.id || null);

// 格式化日期
const formatDate = (date: Date) => {
  return formatPostDate(date);
};

// 获取状态文本
const getStatusText = (status: number) => {
  switch(status) {
    case 0: return '待审核';
    case 1: return '已发布';
    case 2: return '已驳回';
    case 3: return '封禁';
    default: return '全部';
  }
};

// 获取用户帖子列表
const loadUserPosts = async (status = -1) => {
  try {
    loading.value = true;
    // 如果是全部(-1)，则不传递状态参数
    const params: any = {
      page: currentPage.value,
      pageSize: pageSize.value
    };
    
    if (status !== -1) {
      params.status = status;
    }
    
    const response = await getUserPosts(params);
    
    // 处理可能的数据结构差异
    let responseData;
    if (response.data) {
      // 如果响应在外层有一个data包装
      responseData = response.data;
    } else {
      // 如果响应直接就是数据
      responseData = response;
    }
    
    // 检查数据结构，兼容不同格式
    if (responseData && responseData.records) {
      // 标准格式: { records: [...], total: x }
      posts.value = responseData.records || [];
      total.value = responseData.total || 0;
    } else if (Array.isArray(responseData)) {
      // 直接数组格式: [...]
      posts.value = responseData;
      total.value = responseData.length;
    } else if (responseData && responseData.data && responseData.data.records) {
      // 双层包装格式: { data: { records: [...], total: x }}
      posts.value = responseData.data.records || [];
      total.value = responseData.data.total || 0;
    } else {
      // 尝试其他可能的字段名
      const possibleRecordsKeys = ['records', 'data', 'list', 'items'];
      let foundRecords = false;
      
      for (const key of possibleRecordsKeys) {
        if (responseData[key]) {
          posts.value = Array.isArray(responseData[key]) ? responseData[key] : [];
          foundRecords = true;
          break;
        }
      }
      
      if (!foundRecords) {
        posts.value = [];
      }
      
      // 查找总数字段
      const possibleTotalKeys = ['total', 'totalCount', 'count'];
      for (const key of possibleTotalKeys) {
        if (responseData[key] !== undefined) {
          total.value = responseData[key] || 0;
          break;
        }
      }
    }
  } catch (error) {
    console.error('获取用户帖子失败:', error);
    ElMessage.error('获取用户帖子失败');
  } finally {
    loading.value = false;
  }
};

// 打开编辑模态框
const openEditModal = (postId: number) => {
  // 先重置postId，然后在下一个tick设置新值，确保watch触发
  editingPostId.value = null;
  nextTick(() => {
    editingPostId.value = postId;
    editDialogVisible.value = true;
  });
};

// 编辑成功回调
const onEditSuccess = () => {
  editDialogVisible.value = false;
  editingPostId.value = null; // 重置postId
  loadUserPosts(selectedStatus.value);
};

// 模态框关闭回调
const onModalClose = () => {
  editingPostId.value = null; // 重置postId
};

// 删除帖子
const handleDelete = async (postId: number) => {
  try {
    await ElMessageBox.confirm(
      '确定要删除这个帖子吗？此操作不可撤销。',
      '删除确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
    );
    
    // 调用删除帖子的API
    const response = await deletePost(postId);
    
    if (response.code === 1) {
      ElMessage.success('帖子删除成功');
      // 重新加载当前状态的帖子列表
      await loadUserPosts(selectedStatus.value);
    } else {
      ElMessage.error(response.msg || '删除帖子失败');
    }
  } catch (error) {
    // 用户取消删除或发生错误
    if (error !== 'cancel') {
      console.error('删除帖子失败:', error);
      ElMessage.error('删除帖子失败');
    }
  }
};

// 筛选状态
const selectStatus = async (status: number) => {
  selectedStatus.value = status;
  currentPage.value = 1; // 重置到第一页
  await loadUserPosts(status); // 加载指定状态的帖子
};

// 分页改变
const handlePageChange = (page: number) => {
  currentPage.value = page;
  loadUserPosts(selectedStatus.value); // 根据当前选中状态加载帖子
};

onMounted(() => {
  loadUserPosts(); // 页面初始化时加载全部帖子
});

return (_ctx: any,_cache: any) => {
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_pagination = _resolveComponent("el-pagination")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[1] || (_cache[1] = _createElementVNode("h2", { class: "page-title" }, "我的帖子", -1)),
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(statuses, (status) => {
        return _createElementVNode("button", {
          key: status.value,
          class: _normalizeClass(['status-btn', { active: selectedStatus.value === status.value }]),
          onClick: ($event: any) => (selectStatus(status.value))
        }, _toDisplayString(status.label), 11, _hoisted_3)
      }), 64))
    ]),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, "加载中..."))
      : (posts.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, "暂无帖子内容"))
        : (_openBlock(), _createElementBlock("div", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(posts.value, (post) => {
              return (_openBlock(), _createElementBlock("div", {
                key: post.id,
                class: "post-card",
                onClick: ($event: any) => (_ctx.$emit('show-post-detail', post.id))
              }, [
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("img", {
                    src: post.userAvatar,
                    alt: "avatar",
                    class: "avatar"
                  }, null, 8, _hoisted_9),
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("div", _hoisted_11, _toDisplayString(post.name), 1),
                    _createElementVNode("div", _hoisted_12, _toDisplayString(formatDate(new Date(post.createTime))), 1)
                  ]),
                  _createElementVNode("div", {
                    class: _normalizeClass(["post-status", 'status-' + post.status])
                  }, _toDisplayString(getStatusText(post.status)), 3)
                ]),
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("h3", _hoisted_14, _toDisplayString(post.title), 1),
                  _createElementVNode("p", _hoisted_15, _toDisplayString(post.content), 1),
                  (post.coverImage)
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        src: post.coverImage,
                        alt: "cover",
                        class: "post-cover"
                      }, null, 8, _hoisted_16))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_17, [
                  _createElementVNode("span", _hoisted_18, [
                    _createVNode(_component_el_icon, null, {
                      default: _withCtx(() => [
                        _createVNode(_unref(View))
                      ]),
                      _: 1
                    }),
                    _createTextVNode(_toDisplayString(post.viewCount), 1)
                  ]),
                  _createElementVNode("span", _hoisted_19, [
                    _createVNode(_component_el_icon, null, {
                      default: _withCtx(() => [
                        _createVNode(_unref(StarFilled))
                      ]),
                      _: 1
                    }),
                    _createTextVNode(_toDisplayString(post.likeCount), 1)
                  ]),
                  _createElementVNode("span", _hoisted_20, [
                    _createVNode(_component_el_icon, null, {
                      default: _withCtx(() => [
                        _createVNode(_unref(ChatLineRound))
                      ]),
                      _: 1
                    }),
                    _createTextVNode(_toDisplayString(post.commentCount), 1)
                  ]),
                  _createElementVNode("span", _hoisted_21, [
                    _createVNode(_component_el_icon, null, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Collection))
                      ]),
                      _: 1
                    }),
                    _createTextVNode(_toDisplayString(post.favoriteCount), 1)
                  ])
                ]),
                (post.userId === currentUserId.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                      _createElementVNode("button", {
                        class: "edit-btn",
                        onClick: _withModifiers(($event: any) => (openEditModal(post.id)), ["stop"])
                      }, " ✏️ 编辑 ", 8, _hoisted_23),
                      _createElementVNode("button", {
                        class: "delete-btn",
                        onClick: _withModifiers(($event: any) => (handleDelete(post.id)), ["stop"])
                      }, " 🗑️ 删除 ", 8, _hoisted_24)
                    ]))
                  : _createCommentVNode("", true)
              ], 8, _hoisted_7))
            }), 128))
          ])),
    (total.value > pageSize.value)
      ? (_openBlock(), _createBlock(_component_el_pagination, {
          key: 3,
          class: "pagination",
          "current-page": currentPage.value,
          "page-size": pageSize.value,
          total: total.value,
          layout: "prev, pager, next",
          onCurrentChange: handlePageChange
        }, null, 8, ["current-page", "page-size", "total"]))
      : _createCommentVNode("", true),
    _createVNode(EditPostModal, {
      modelValue: editDialogVisible.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((editDialogVisible).value = $event)),
      "post-id": editingPostId.value,
      onSaveSuccess: onEditSuccess,
      onClose: onModalClose
    }, null, 8, ["modelValue", "post-id"])
  ]))
}
}

})